import { useMemo, useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useZodForm } from '../../../../common/FormWrapper';
import { useAuth } from '../../../../contexts/AuthContext';
import { useGetAllTeams } from '../../../../hooks/useGetAllTeams';
import { formatTime } from '../../../../utils/dateUtils';
import {
  TeamSettingsFormData,
  teamSettingsSchema,
} from '../../constants/teamSettingsSchema';

import { useGetAllUsers } from './useGetAllUsers';
import { useGetTeamDetails } from './useGetTeamDetails';
import { useUpdateTeam } from './useUpdateTeamDetails';

export type Option = {
  id: string;
  label: string;
  value: string;
};

export type TeamSettingsReturn = {
  form: UseFormReturn<TeamSettingsFormData>;
  isEditOn: boolean;
  setIsEditOn: (value: boolean) => void;
  userOptions: Option[];
  teamOptions: Option[];
  handleSave: () => Promise<void>;
  isLoadingTeams?: boolean;
  isLoadingDetails?: boolean;
  isLoadingUsers?: boolean;
};

export function useTeamSettings(): TeamSettingsReturn {
  const [isEditOn, setIsEditOn] = useState(false);
  const { userData } = useAuth();
  const form = useZodForm<TeamSettingsFormData>({
    schema: teamSettingsSchema,
    defaultValues: {
      teamName: { id: '', label: '', value: '' },
      teamLead: { id: '', label: '', value: '' },
      accountManager: { id: '', label: '', value: '' },
      workHoursStart: '',
      workHoursEnd: '',
      teamMembers: [],
      checkInTimes: [],
    },
  });

  const { data: users, isLoading: isLoadingUsers } = useGetAllUsers();
  const { data: teams, isLoading: isLoadingTeams } = useGetAllTeams();
  const teamOptions = useMemo(
    () =>
      teams?.map((team) => ({
        id: team.id,
        label: team.name,
        value: team.id,
      })) ?? [],
    [teams],
  );

  const userOptions = useMemo(
    () =>
      users?.map((user) => ({
        id: user.id,
        label: `${user.first_name} ${user.last_name}`,
        value: user.id,
      })) ?? [],
    [users],
  );

  useEffect(() => {
    if (teamOptions.length > 0 && !form.getValues('teamName')?.value) {
      form.setValue(
        'teamName',
        teamOptions.find((team) => team.id === userData?.teamId) ??
          teamOptions[0],
      );
    }
  }, [teamOptions, form, userData]);

  const selectedTeam = form.watch('teamName');
  const selectedTeamId = useMemo(
    () => selectedTeam?.value,
    [selectedTeam?.value],
  );
  const { data: teamDetails, isLoading: isLoadingDetails } = useGetTeamDetails(
    selectedTeamId ?? '',
  );

  useEffect(() => {
    if (teamDetails) {
      form.reset({
        teamName: {
          id: teamDetails.id,
          label: teamDetails.name,
          value: teamDetails.id,
        },
        accountManager: teamDetails.account_manager
          ? {
              id: teamDetails.account_manager.id,
              label: `${teamDetails.account_manager.first_name || ''} ${
                teamDetails.account_manager.last_name || ''
              }`,
              value: teamDetails.account_manager.id,
            }
          : undefined,
        teamLead: teamDetails.team_lead
          ? {
              id: teamDetails.team_lead.id,
              label: `${teamDetails.team_lead.first_name || ''} ${
                teamDetails.team_lead.last_name || ''
              }`,
              value: teamDetails.team_lead.id,
            }
          : undefined,
        workHoursStart: teamDetails.work_hours_start,
        workHoursEnd: teamDetails.work_hours_end,
        teamMembers: teamDetails.team_members.map((member) => ({
          id: member.id,
          label: `${member.first_name} ${member.last_name}`,
          value: member.id,
        })),
        checkInTimes:
          teamDetails.check_in_times?.map((time) => ({
            id: time,
            label: formatTime(time),
            value: time,
            time,
          })) || [],
      });
    }
  }, [teamDetails, form]);

  const updateTeam = useUpdateTeam();

  const handleSave = async (): Promise<void> => {
    const formValues = form.getValues();
    const initialValues = teamDetails
      ? {
          accountManager: teamDetails.account_manager?.id,
          teamLead: teamDetails.team_lead?.id,
          workHoursStart: teamDetails.work_hours_start,
          workHoursEnd: teamDetails.work_hours_end,
          teamMembers: teamDetails.team_members.map((m) => m.id),
          checkInTimes: teamDetails.check_in_times,
        }
      : null;

    const isEdited =
      initialValues &&
      (formValues.accountManager?.value !== initialValues.accountManager ||
        formValues.teamLead?.value !== initialValues.teamLead ||
        formValues.workHoursStart !== initialValues.workHoursStart ||
        formValues.workHoursEnd !== initialValues.workHoursEnd ||
        JSON.stringify(formValues.teamMembers.map((m) => m.value)) !==
          JSON.stringify(initialValues.teamMembers) ||
        JSON.stringify(formValues.checkInTimes.map((t) => t.time)) !==
          JSON.stringify(initialValues.checkInTimes));

    if (!isEdited) {
      toast.error('No changes detected');
      return;
    }
    const sortedCheckInTimes = formValues.checkInTimes.sort((a, b) => {
      const parseTime = (timeString: string): Date => {
        const [time, modifier] = timeString.split(' ');
        // eslint-disable-next-line prefer-const
        let [hours, minutes] = time.split(':').map(Number);
        if (modifier === 'PM' && hours !== 12) {
          hours += 12;
        } else if (modifier === 'AM' && hours === 12) {
          hours = 0;
        }
        return new Date(1970, 0, 1, hours, minutes);
      };

      const dateA = parseTime(a.value);
      const dateB = parseTime(b.value);

      return dateA.getTime() - dateB.getTime();
    });
    try {
      await updateTeam.mutateAsync({
        teamId: selectedTeamId,
        accountManagerId: formValues.accountManager?.value,
        teamLeadId: formValues.teamLead?.value,
        workHoursStart: formValues.workHoursStart,
        workHoursEnd: formValues.workHoursEnd,
        teamMembers: formValues.teamMembers.map((member) => member.value),
        checkInTimes: sortedCheckInTimes.map((time) => formatTime(time.value)),
      });
      toast.success('Team updated successfully');
    } catch (error) {
      toast.error('Failed to update team');
      console.error(error);
      throw error; // Re-throw to handle in the component
    }
  };

  return {
    form,
    isEditOn,
    setIsEditOn,
    userOptions,
    teamOptions,
    isLoadingTeams,
    isLoadingDetails,
    isLoadingUsers,
    handleSave,
  };
}
